class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });

    setTimeout(() => {
      initPostcodeRestriction();
    }, 0);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    if (!target || !target.classList.contains('quantity__input')) return;

    const inputValue = parseInt(target.value);
    const index = target.dataset.index;
    let message = '';

    if (inputValue < target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', target.dataset.min);
    } else if (target.max && inputValue > parseInt(target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', target.max);
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement ? document.activeElement.getAttribute('name') : undefined,
        target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          initPostcodeRestriction();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }
          initPostcodeRestriction();
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems) {
      sections.unshift({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (mainCartFooter) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const sectionContainer = document.getElementById(section.id);
            if (!sectionContainer) return;

            const elementToReplace =
              sectionContainer.querySelector(section.selector) || sectionContainer;

            const sectionHtml = parsedState.sections[section.section];
            if (!sectionHtml) return;

            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          });

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';

          if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }

          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

          if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }

          initPostcodeRestriction();
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const text = lineItemError.querySelector('.cart-item__error-text');
      if (text) text.textContent = message;
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) {
      document.activeElement.blur();
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

function normalizePostcode(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^\d]/g, '');
}

function getBlockedExactPostcodes() {
  if (
    window.postcodeRestrictionConfig &&
    Array.isArray(window.postcodeRestrictionConfig.blockedExactPostcodes)
  ) {
    return window.postcodeRestrictionConfig.blockedExactPostcodes;
  }

  return [
    '25530', '25537', '25538', '25539', '25540', '25549',
    '25550', '25551', '25552', '25553', '25554', '25555',
    '25556', '25557', '25558', '25560'
  ];
}

function getBlockedPrefixes() {
  if (
    window.postcodeRestrictionConfig &&
    Array.isArray(window.postcodeRestrictionConfig.blockedPrefixes)
  ) {
    return window.postcodeRestrictionConfig.blockedPrefixes;
  }

  return ['07', '35', '38', '51', '52'];
}

function isBlockedPostcode(postcode) {
  const normalized = normalizePostcode(postcode);
  if (normalized.length !== 5) return false;

  if (getBlockedExactPostcodes().includes(normalized)) {
    return true;
  }

  return getBlockedPrefixes().some((prefix) => normalized.startsWith(prefix));
}

function getRestrictionMessage() {
  if (
    window.postcodeRestrictionConfig &&
    typeof window.postcodeRestrictionConfig.message === 'string' &&
    window.postcodeRestrictionConfig.message.length
  ) {
    return window.postcodeRestrictionConfig.message;
  }

  return 'No enviamos cerveza a este código postal. Selecciona recogida en tienda o usa una dirección fuera de la zona restringida.';
}

function setButtonDisabled(button, disabled) {
  if (!button) return;
  button.disabled = disabled;
  button.classList.toggle('is-disabled-by-postcode', disabled);
}

function setContainerMessage(messageElement, disabled) {
  if (!messageElement) return;
  messageElement.textContent = getRestrictionMessage();
  messageElement.classList.toggle('is-visible', disabled);
}

function applyRestrictionToMainCart(disabled) {
  const checkoutButton = document.getElementById('checkout');
  const dynamicButtons = document.querySelector('.cart__dynamic-checkout-buttons');
  const message = document.getElementById('CartPostalCodeMessage');

  setButtonDisabled(checkoutButton, disabled);

  if (dynamicButtons) {
    dynamicButtons.classList.toggle('is-disabled-by-postcode', disabled);
  }

  setContainerMessage(message, disabled);
}

function applyRestrictionToDrawer(disabled) {
  const checkoutButton = document.getElementById('CartDrawer-Checkout');
  const message = document.getElementById('CartDrawerPostalCodeMessage');

  setButtonDisabled(checkoutButton, disabled);
  setContainerMessage(message, disabled);
}

function handleSinglePostcodeInput(inputId, applyFn) {
  const input = document.getElementById(inputId);
  if (!input) return;

  const value = normalizePostcode(input.value);
  const disabled = value.length === 5 && isBlockedPostcode(value);
  applyFn(disabled);
}

function bindPostcodeInput(inputId, handler) {
  const input = document.getElementById(inputId);
  if (!input) return;

  if (!input.dataset.postcodeRestrictionBound) {
    input.addEventListener('input', handler);
    input.addEventListener('change', handler);
    input.dataset.postcodeRestrictionBound = 'true';
  }
}

function handleMainCartRestriction() {
  handleSinglePostcodeInput('CartPostalCode', applyRestrictionToMainCart);
}

function handleDrawerRestriction() {
  handleSinglePostcodeInput('CartDrawerPostalCode', applyRestrictionToDrawer);
}

function initPostcodeRestriction() {
  bindPostcodeInput('CartPostalCode', handleMainCartRestriction);
  bindPostcodeInput('CartDrawerPostalCode', handleDrawerRestriction);

  handleMainCartRestriction();
  handleDrawerRestriction();
}

document.addEventListener('DOMContentLoaded', initPostcodeRestriction);
document.addEventListener('shopify:section:load', initPostcodeRestriction);